
import DialogTemplate from '@/_components/dialog/template/template.vue'
import CompanyInfoMixin from '@/_mixins/companyInfo.mixin.ts'
import PhoneInput from '@/_components/input/phone/phone.vue'
import CompanyAutocomplete from '@/components/Autocomplete/company.vue'
import { CREATE_VIRTUAL_CARD, CREATE_ACCESS_GROUP_CARD_ASSOCIATION } from './gql/mutations'
import { CARD_ID_VALIDATION_QUERY, MOBILE_NUMBER_VALIDATION_QUERY } from './gql/queries'
import { accessClient } from '@/apolloClient'

export default {
    name: 'AccessControlUserRuleDialog',
    mixins: [CompanyInfoMixin],
    components: { CompanyAutocomplete, DialogTemplate, PhoneInput },
    props: {
        props: {
            type: Object,
            required: true,
        },
    },
    data() {
        const isVirtualCard = this.props.cardId && this.props.cardId[0] === '+'
        const cardId = isVirtualCard ? '' : this.props.cardId || null

        return {
            isFormValid: false,
            isAdding: false,
            cardType: isVirtualCard ? 'maybeVirtual' : 'normal',
            card: {
                id: cardId,
                firstName: '',
                lastName: '',
                mobile: isVirtualCard ? this.props.cardId : '',
                company: null,
            },
            selectedGroups: this.props.selectedGroups ?? [],
            cardError: null,
        }
    },
    computed: {
        groups() {
            return this.props.groups || []
        },
        canAddCard() {
            return this.isFormValid && this.selectedGroups.length > 0
        },
    },
    methods: {
        handleVirtualCardTypeChanged() {
            this.cardType = 'maybeVirtual'
            this.card.id = ''
            setTimeout(() => {
                this.$refs.mobileInput?.focus()
            })
        },
        validateCard(cardId: string) {
            return accessClient.query({
                query: CARD_ID_VALIDATION_QUERY,
                variables: { value: cardId },
                fetchPolicy: 'network-only',
            })
        },
        validateMobileNumber() {
            return accessClient.query({
                query: MOBILE_NUMBER_VALIDATION_QUERY,
                variables: { value: this.card.mobile },
                fetchPolicy: 'network-only',
            })
        },
        handleAdd() {
            if (this.cardType === 'virtual') {
                this.addVirtualCard()
            } else {
                this.addCardToGroup(this.card.id || this.card.mobile)
            }
        },
        showErrors(message: string) {
            this.$store.commit('setSuccessSnackBar', { subtitle: message, timeout: 5000 })
        },
        async addCardToGroup(cardId: string, cardExists = false) {
            try {
                if (!this.canAddCard) {
                    return
                }

                this.isAdding = true
                const phoneValidationResult = await this.validateCard(cardId)
                if (phoneValidationResult.data?.validate?.cardId == null) {
                    this.isAdding = false
                    if (this.cardType === 'maybeVirtual') {
                        this.cardError = this.$t(`fe_validation.error_valid_phone`)
                    } else {
                        this.cardError = this.$t(`fe_access["text.card_id_invalid"]`)
                    }
                    return
                }

                let results: any[] = []

                if (cardExists) {
                    results = await Promise.all(
                        this.selectedGroups.map((groupId) =>
                            accessClient.mutate({
                                mutation: CREATE_ACCESS_GROUP_CARD_ASSOCIATION,
                                variables: { cardId, groupId, withPsi: this.props.withPsi },
                            })
                        )
                    )
                } else {
                    // check first group to check if card exists
                    const groups = [...this.selectedGroups]
                    const [firstGroupId] = groups.splice(0, 1)
                    const result = await accessClient.mutate({
                        mutation: CREATE_ACCESS_GROUP_CARD_ASSOCIATION,
                        variables: { cardId, groupId: firstGroupId, withPsi: this.props.withPsi },
                    })
                    results.push(result)

                    // stop if card not found and is virtual card
                    if (Array.isArray(result.data.createAccessGroupCardAssociation.errors)) {
                        const cardNotFoundError =
                            result.data.createAccessGroupCardAssociation.errors.find(
                                (error: any) => error.__typename === 'CardNotFoundError'
                            )
                        if (cardNotFoundError && cardNotFoundError.isVirtual) {
                            this.cardType = 'virtual'
                            if (!this.card.mobile) {
                                this.card.mobile = cardId
                            }
                            setTimeout(() => {
                                this.$refs.firstNameInput?.focus()
                            })
                        }

                        this.showErrors(
                            result.data.createAccessGroupCardAssociation.errors
                                .map((error: any) => error.message)
                                .join('\n')
                        )
                        return
                    }

                    const otherResults = await Promise.all(
                        groups.map((groupId) =>
                            accessClient.mutate({
                                mutation: CREATE_ACCESS_GROUP_CARD_ASSOCIATION,
                                variables: { cardId, groupId, withPsi: this.props.withPsi },
                            })
                        )
                    )
                    results.push(...otherResults)
                }

                if (
                    results.some((results) =>
                        Array.isArray(results.data.createAccessGroupCardAssociation.errors)
                    )
                ) {
                    this.showErrors(
                        results
                            .map((result: any) =>
                                result.data.createAccessGroupCardAssociation.errors
                                    .map((error: any) => error.message)
                                    .join('\n')
                            )
                            .join('\n')
                    )
                    return
                }

                this.$emit('close', {
                    cardAdded: results.map(
                        (result: any) => result.data.createAccessGroupCardAssociation.association
                    ),
                })
            } finally {
                this.isAdding = false
            }
        },
        async addVirtualCard() {
            try {
                if (!this.card.company) {
                    return
                }

                this.isAdding = true
                const phoneValidationResult = await this.validateMobileNumber()
                if (phoneValidationResult.data?.validate?.mobileNumber == null) {
                    this.isAdding = false
                    this.cardError = this.$t(`fe_validation.error_valid_phone`)
                    return
                }

                const result = await accessClient.mutate({
                    mutation: CREATE_VIRTUAL_CARD,
                    variables: {
                        firstName: this.card.firstName,
                        lastName: this.card.lastName,
                        mobile: this.card.mobile,
                        companyId: this.card.company.id,
                        projectId: this.currentProjectId,
                        withPsi: this.props.withPsi,
                    },
                })

                if (Array.isArray(result.data.createVirtualCard.errors)) {
                    const cardExistsError = result.data.createVirtualCard.errors.find(
                        (error) => error.__typename === 'CardAlreadyExistsError'
                    )
                    if (!cardExistsError) {
                        this.showErrors(
                            result.data.createVirtualCard.errors
                                .map((error) => error.message)
                                .join('\n')
                        )
                        return
                    }
                }
                await this.addCardToGroup(this.card.mobile, true)

                this.$emit('close', {
                    cardCreated:
                        result.data.createVirtualCard.query
                            .allAccessGroupCardAssociationsForProject,
                })
            } finally {
                this.isAdding = false
            }
        },
    },
}
